import loadable from "@loadable/component";
import { inject } from "mobx-react";
import React from "react";
import type { Store } from "../../../../client/stores";
import type { BlockFragment } from "../../generated/webapi.g";
import { FeedResultFragment, SitesQueryDatasource } from "../../generated/webapi.g";
import { blockHaveAd, showLoadMoreAd } from "../../lib/ads";
import { isFeedResult, isLeadsCard, isPublishRecord } from "../../lib/guards";
import { FeedDisplayHints } from "../../services/Sites.utils";
import { Grid, Heading } from "../../ui";
import { HeadingProps } from "../../ui/Heading.component";
import { AdPosition } from "../Ad.component";
import "./Datasource.style.scss";

const LeadFeedItem = loadable(() => import("./LeadFeedItem.component"));
const ContentFeedItem = loadable(() => import("./ContentFeedItem.component"));

interface DatasourceProps {
    content: FeedResultFragment[] | null;
    block: BlockFragment;
    feedId: string;
    displayHints: FeedDisplayHints;
    store?: Store;
    title: string;
    heading?: HeadingProps;
}

export const DatasourceFeedItem = ({
    item,
    index,
    showEyebrow,
    displayHints,
}: {
    item: FeedResultFragment;
    index: number;
    showEyebrow: boolean;
    displayHints: FeedDisplayHints;
}) => {
    const { style } = displayHints;
    if (!isFeedResult(item)) {
        return null;
    }

    switch (item.type) {
        case SitesQueryDatasource.Publishing:
            return isPublishRecord(item.record) ? (
                <ContentFeedItem item={item.record} style={style} index={index} showEyebrow={showEyebrow} />
            ) : null;
        case SitesQueryDatasource.Leads:
            return isLeadsCard(item.record) ? (
                <LeadFeedItem displayHints={displayHints} lead={item.record} index={index} />
            ) : null;
        default:
            return null;
    }
};

@inject("store")
export class Datasource extends React.Component<DatasourceProps> {
    constructor(props: DatasourceProps) {
        super(props);
    }

    render() {
        if (!this.props.content) {
            return null;
        }
        const { title, content, feedId, displayHints, block } = this.props;

        if (!content.length) {
            return null;
        }

        const heading: HeadingProps = this.props.heading ?? { level: 2 };
        const adBlockData = { value: { ...block.value } } as BlockFragment;
        const { showEyebrow, enableLoadMore } = displayHints;
        const adInsertPosition = block.value.size || 10;
        const haveAds = blockHaveAd(block.value.position);

        return (
            <React.Fragment>
                {title ? <Heading {...heading}>{title}</Heading> : null}
                <div className="feed-cards" data-block-id={`${block.id}`}>
                    <Grid columns={displayHints.columns}>
                        {content.map((item, i) => {
                            const showAd = showLoadMoreAd(content.length, adInsertPosition, i, enableLoadMore);
                            return (
                                <React.Fragment key={`feed-item-${feedId}-${(item as any).ref_id}-${i}`}>
                                    <DatasourceFeedItem
                                        item={item}
                                        displayHints={displayHints}
                                        index={i}
                                        showEyebrow={showEyebrow}
                                    />
                                    {haveAds && showAd ? (
                                        <div className="load-more-ad">
                                            <AdPosition
                                                block={adBlockData}
                                                batchId={`${feedId}-${Math.ceil(i / adInsertPosition)}`}
                                            />
                                        </div>
                                    ) : null}
                                </React.Fragment>
                            );
                        })}
                    </Grid>
                </div>
            </React.Fragment>
        );
    }
}

export default Datasource;
